import React from 'react';
import styled from '@emotion/styled';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { useHistory } from 'react-router';
import {
  useButton,
  useDispatch,
  useKitchenMessage,
  useLicenses,
  useSelector,
  useStaffCan,
} from '~Hooks';
import * as actions from '~actions';
import TableButton from '../TableButton';
import Button from '../../../../ComponentsImproved/Button';
import Send from '~/Assets/icons/send.svg';
import PredefinedMessages, { Message } from '~Components/PredefinedMessages';
import SystemStatus from '~Components/SystemStatus';
import { useI18n } from '~/Hooks';
import _ from 'lodash';
import { addToast } from '~/actions';
import { PrintBill } from './PrintBill';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import PrinterStatus from '~Components/PrinterStatus';
import CompetitionStatus from '~Components/CompetitionStatus';
import { useCompetition } from '~Providers/CompetitionsProvider';
import { useStorage } from '~Components/Storage';
import IncomingOrdersIndicator from '~Scenes/IncomingAppOrders/IncomingOrdersIndicator';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import EditChequeActions from './Components/EditChequeActions';
import { BlueButton } from '~Styles';
import { useOrderStatusApi, useOrderStatusUniverse } from '~Components/OrderStatusUniverse';

const OuterWrapper = styled.div`
  position: relative;
`;

const Wrapper = styled.div`
  height: 64px;
  padding: 0 8px;
  background-color: #0e1216;
  box-shadow: 0px 0px 64px 0px rgba(0, 0, 0, 0.64);
  margin-top: auto;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const ButtonWrapper = styled.div`
  margin-left: 8px;
  flex: 1;
  max-width: 200px;
`;

const MenuButtonWrapper = styled.div`
  height: 100%;
  width: 50px;
  margin-right: auto;
  z-index: 100;
`;

const MenuButton = styled.div`
  height: 100%;
  width: 100%;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
`;

const Icon = styled.i`
  color: #8b8f93;
  font-size: 24px;
  height: 24px;
  width: 24px;
  text-align: center;
  line-height: 23px;
`;

const PrinterStatusWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;

  div {
    border-top: none;
  }
`;

const OpenButton = styled(BlueButton)`
  font-size: 16px;
`;

interface Props {
  selectedCheque: string | null;
  selectedTable: { id: string; name: string } | null;
  showBong: () => void;
  showLogin: () => void;
  disableOrdering?: boolean;
}

const Footer: React.FunctionComponent<Props> = ({
  selectedCheque,
  selectedTable,
  showBong,
  showLogin,
  disableOrdering = false,
}) => {
  const [storage] = useStorage();
  const staffId = useSelector((state) =>
    state.companyStaff?.staffs.length ? state.staff : false,
  );
  const staffCan = useStaffCan();
  const dispatch = useDispatch();
  const history = useHistory();
  const api = useTableApi();
  const universe = useTableUniverse();
  const licenses = useLicenses();
  const competitions = useCompetition();
  const { isShowing: showMessage, toggle: showMessageToggle } = useModal();
  const showIcon = window.innerWidth > 800;
  const { i18n } = useI18n();
  const sendKitchenMessage = useKitchenMessage();
  const venueId = useSelector((state) => state.venueId);
  const orderStatusApi = useOrderStatusApi(venueId || '');
  const orderStatusUniverse = useOrderStatusUniverse();

  const predefinedMessages = useSelector((state) => state.predefinedMessages);
  const courseTypes = useSelector((state) => state.courseTypes);
  const selectedKDSStation = useSelector((state) => state.stations)?.find(
    (s) => s.id === storage.selectedKDSStation,
  );

  const staffCanMutate = selectedCheque && staffCan.mutateCheque(selectedCheque);

  const sortedTypes = [...courseTypes]?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const cheque = universe.cheques.find((c) => c.id === selectedCheque);
  const chequeItems = universe.items.filter((c) => c.chequeId === selectedCheque);

  const messages: string[] = [];
  predefinedMessages.map((item) => item.type === 'CHEQUE' && messages.push(item.message));

  const handleSendMessage = (value: string | Message) => {
    if (!selectedCheque) {
      // Never happens
      return;
    }

    const strToSend =
      typeof value === 'string' ? value : `${i18n('TSR.NextCourse.Short')} ${value.label}`;

    let items = chequeItems;

    // If we've selected a course type, move the cheque to that course and filter items for that course type
    if (typeof value !== 'string') {
      api.progressCheque(selectedCheque, value.label);
      api.addMessage(strToSend, selectedCheque);
      items = items.filter((i) => i.courseId === value.id);
      // api.log.changeCourse(selectedCheque, selectedTable?.id ?? '', value.label);

      // Fire all KDS orders for this table + course. The event carries the
      // criteria and every device resolves matching orders in the reducer, so
      // this works even when this device's local universe is missing the order
      // (previously this filtered the local universe and silently fired nothing).
      const courseId = value.id;
      const tableId = selectedTable?.id;
      if (tableId) {
        orderStatusApi.fireCourse(courseId, { tableId, tableName: selectedTable?.name });
      }
    }

    sendKitchenMessage({
      tableName: selectedTable?.name ?? '',
      message: strToSend,
      items: items.map((item) => ({ itemId: item.itemId, categoryId: item.categoryId })),
    });

    dispatch(addToast(i18n('TOAST.Message.Sent'), true));

    showMessageToggle();
  };

  const messagesList = [
    ...sortedTypes.map((type) => ({
      id: type.id,
      label: type.label,
    })),
    ...messages,
  ];

  const chequeMessages = universe.messages.filter((m) => m.chequeId === selectedCheque);
  const disabledMessages = messagesList.reduce((acc: string[], message) => {
    if (typeof message === 'string') {
      return acc;
    }

    // If the message equals the current stage, disable it
    if (cheque?.stage === message.label) {
      return [...acc, message.label];
    }

    // Remove the word "Next/Framma" from the message to know it is a course
    const formattedChequeMessages = chequeMessages.map((m) =>
      m.message.replace(i18n('TSR.NextCourse.Short'), '').trim(),
    );

    // If the course has already been sent to the kitchen, disable it
    if (formattedChequeMessages.includes(message.label)) {
      return [...acc, message.label];
    }

    return acc;
  }, []);

  const shouldShowIncomingAppOrders =
    storage.showIncomingAppOrders ||
    selectedKDSStation?.orderStatuses.includes(ORDER_STATUS.PENDING);

  const openButton = useButton({
    onClick: () => (staffId !== null ? showBong() : showLogin()),
    disabled: disableOrdering || !staffCan.ADD_ITEM_TO_TABLE,
    disabledToast: !staffCan.ADD_ITEM_TO_TABLE
      ? i18n('General.MissingPermission')
      : disableOrdering
      ? i18n('TOAST.DisableOrderingAfterPayment')
      : undefined,
  });

  return (
    <OuterWrapper>
      <Wrapper>
        <MenuButtonWrapper>
          {staffCan.openMainMenu ? (
            <MenuButton
              onClick={() => {
                dispatch(actions.toggleMainMenu(true));
              }}>
              <Icon className="icon hamburger_wide" />
            </MenuButton>
          ) : (
            <MenuButton
              onClick={() => {
                history.push('gateway');
              }}></MenuButton>
          )}
        </MenuButtonWrapper>
        {selectedCheque && staffCanMutate ? (
          <>
            <ButtonWrapper>
              <OpenButton {...openButton} fluid size="large">
                {i18n('TM.Open')}
              </OpenButton>
            </ButtonWrapper>

            <ButtonWrapper>
              <TableButton
                icon={showIcon && Send}
                onTouchEnd={() => (staffId !== null ? showMessageToggle() : showLogin())}>
                {i18n('TM.Message')}
              </TableButton>
            </ButtonWrapper>

            <EditChequeActions selectedCheque={selectedCheque} showLogin={showLogin} />

            <PrintBill
              selectedCheque={selectedCheque}
              selectedTable={selectedTable}
              showLogin={showLogin}
              staffId={staffId}
            />
          </>
        ) : (
          <PrinterStatusWrapper>
            {competitions.onGoing.length > 0 && <CompetitionStatus />}
            {licenses.hasApp && shouldShowIncomingAppOrders && <IncomingOrdersIndicator />}
            <SystemStatus />
            <PrinterStatus />
          </PrinterStatusWrapper>
        )}
      </Wrapper>

      {selectedCheque && (
        <Modal isShowing={showMessage} hide={showMessageToggle} backdropClose={false}>
          <PredefinedMessages
            onSubmit={handleSendMessage}
            messages={messagesList ?? []}
            hideTextarea
            width={500}
            disabledMessages={disabledMessages}
          />
          <Button
            width="152px"
            text={i18n('General.Cancel')}
            onTouchEnd={showMessageToggle}
            styleType="Warning"
          />
        </Modal>
      )}
    </OuterWrapper>
  );
};

export default React.memo(Footer);
